import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import dayjs from 'dayjs';

import { CaixaConstasService, IResumoFluxo } from '../../../shared/services/api/Financeiro/Caixa';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';
import { imprimirFluxoContas } from './ImpressaoFluxoContas';

// Relatório do fluxo de TODAS as contas — aberto pelo Imprimir da Conciliação
// quando nenhuma conta está selecionada. Mostra, no período, quanto entrou e
// quanto saiu em cada forma de pagamento (Cartão Crédito, Cartão Débito, PIX,
// Dinheiro, Boleto...) e em cada conta; dá pra desmarcar formas antes de
// imprimir. Transferências entre contas ficam fora dos totais (ver
// ResumoFluxo.ts no backend).

const TIPO_CONTA: Record<string, string> = {
  banco: 'Banco', caixa: 'Caixa', cofre: 'Cofre', recebiveis: 'Recebíveis',
};

// DD/MM/YYYY (filtro da Conciliação) → YYYY-MM-DD.
const paraIso = (data: string) => {
  const partes = (data || '').split('/');
  return partes.length === 3 ? `${partes[2]}-${partes[1]}-${partes[0]}` : dayjs().format('YYYY-MM-DD');
};

interface IDialogRelatorioFluxoProps {
  open: boolean;
  onClose: () => void;
  dataInicial: string; // DD/MM/YYYY — período pesquisado na Conciliação
  dataFinal: string;
}

export const DialogRelatorioFluxo: React.FC<IDialogRelatorioFluxoProps> = ({ open, onClose, dataInicial, dataFinal }) => {
  const theme = useTheme();
  const telaPequena = useMediaQuery(theme.breakpoints.down('sm'));

  const [de, setDe] = useState(paraIso(dataInicial));
  const [ate, setAte] = useState(paraIso(dataFinal));
  const [dados, setDados] = useState<IResumoFluxo | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Formas desmarcadas (por nome) — ficam fora dos totais e da impressão.
  const [desmarcadas, setDesmarcadas] = useState<Set<string>>(new Set());

  const buscar = (inicio = de, fim = ate) => {
    if (!inicio || !fim) return;
    setCarregando(true);
    setErro(null);
    CaixaConstasService.getResumoFluxo(inicio, fim).then((r) => {
      setCarregando(false);
      if (r instanceof Error) { setErro(r.message); return; }
      setDados(r);
      setDesmarcadas(new Set());
    });
  };

  // A cada abertura, começa no período da Conciliação.
  useEffect(() => {
    if (!open) return;
    const inicio = paraIso(dataInicial);
    const fim = paraIso(dataFinal);
    setDe(inicio);
    setAte(fim);
    buscar(inicio, fim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formas = useMemo(() => dados?.porForma ?? [], [dados]);
  const formasMarcadas = useMemo(() => formas.filter((f) => !desmarcadas.has(f.forma)), [formas, desmarcadas]);
  const totalEntrada = formasMarcadas.reduce((s, f) => s + f.entrada, 0);
  // Saída da forma inclui o que foi transferido da conta de recebíveis pro
  // banco (o saldo da forma mostra o que ainda não caiu); nos totais o
  // transferido fica à parte, porque não é dinheiro saindo da empresa.
  const totalTransferido = formasMarcadas.reduce((s, f) => s + f.transferido, 0);
  const totalSaida = formasMarcadas.reduce((s, f) => s + f.saida, 0) - totalTransferido;

  const alternar = (forma: string) => {
    setDesmarcadas((atual) => {
      const novo = new Set(atual);
      if (novo.has(forma)) novo.delete(forma); else novo.add(forma);
      return novo;
    });
  };
  const todasMarcadas = formas.length > 0 && desmarcadas.size === 0;
  const alternarTodas = () => setDesmarcadas(todasMarcadas ? new Set(formas.map((f) => f.forma)) : new Set());

  const imprimir = () => {
    if (!dados) return;
    imprimirFluxoContas({
      datainicial: dados.datainicial,
      datafinal: dados.datafinal,
      formas: formasMarcadas,
      contas: dados.porConta.filter((c) => c.entrada || c.saida || c.transferenciaEntrada || c.transferenciaSaida),
      transferencias: dados.totais.transferencias,
    });
  };

  const quadro = (titulo: string, valor: number, cor: string) => (
    <Box p={1.5} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{titulo}</Typography>
      <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        R$ {formatarMoeda(valor)}
      </Typography>
    </Box>
  );

  const contasComMovimento = (dados?.porConta ?? []).filter((c) => c.entrada || c.saida || c.transferenciaEntrada || c.transferenciaSaida);
  const celNum = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={telaPequena} scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Relatório do fluxo das contas
        <Typography variant="body2" color="text.secondary">
          Recebimentos e saídas de todas as contas, por forma de pagamento
        </Typography>
        <IconButton onClick={onClose} aria-label="Fechar" sx={{ position: 'absolute', right: 10, top: 10 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: { xs: 1.5, sm: 3 } }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={de} onChange={(e) => setDe(e.target.value)} sx={{ minWidth: 150 }} />
          <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={ate} onChange={(e) => setAte(e.target.value)} sx={{ minWidth: 150 }} />
          <Button
            variant="outlined"
            startIcon={carregando ? <CircularProgress size={16} /> : <SearchRoundedIcon />}
            disabled={carregando || !de || !ate}
            onClick={() => buscar()}
          >
            Pesquisar
          </Button>
        </Box>

        {erro && <Alert severity="error">{erro}</Alert>}

        <Box display="grid" gap={1.25} sx={{ gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' } }}>
          {carregando && !dados
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={72} />)
            : (
              <>
                {quadro('Total recebido', totalEntrada, theme.palette.success.main)}
                {quadro('Total de saídas', totalSaida, theme.palette.error.main)}
                {quadro('Transferido p/ banco', totalTransferido, theme.palette.info.main)}
                {quadro('Saldo do período', totalEntrada - totalSaida, totalEntrada - totalSaida >= 0 ? theme.palette.text.primary : theme.palette.error.main)}
              </>
            )}
        </Box>

        {/* Por forma de pagamento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Por forma de pagamento</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Desmarque uma forma para tirá-la dos totais e da impressão.
          </Typography>
          <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={todasMarcadas} indeterminate={desmarcadas.size > 0 && desmarcadas.size < formas.length} onChange={alternarTodas} />
                  </TableCell>
                  <TableCell>Forma</TableCell>
                  <TableCell align="right">Recebido</TableCell>
                  <TableCell align="right">Saída</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Lanç.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {carregando && !dados && Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={6}><Skeleton /></TableCell></TableRow>
                ))}
                {dados && formas.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">Nenhum lançamento no período.</Typography></TableCell></TableRow>
                )}
                {formas.map((f) => {
                  const marcada = !desmarcadas.has(f.forma);
                  return (
                    <TableRow key={f.forma} hover onClick={() => alternar(f.forma)} sx={{ cursor: 'pointer', opacity: marcada ? 1 : 0.45 }}>
                      <TableCell padding="checkbox"><Checkbox size="small" checked={marcada} /></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{f.forma}</TableCell>
                      <TableCell align="right" sx={{ ...celNum, color: 'success.main' }}>{formatarMoeda(f.entrada)}</TableCell>
                      <TableCell align="right" sx={{ ...celNum, color: 'error.main' }}>
                        {formatarMoeda(f.saida)}
                        {(f.transferido > 0 || f.taxa > 0) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[f.transferido > 0 ? `transf. ${formatarMoeda(f.transferido)}` : '', f.taxa > 0 ? `taxa ${formatarMoeda(f.taxa)}` : ''].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ ...celNum, fontWeight: 700 }}>{formatarMoeda(f.entrada - f.saida)}</TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{f.lancamentos}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Por conta */}
        {contasComMovimento.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Por conta</Typography>
            <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Conta</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Entrada</TableCell>
                    <TableCell align="right">Saída</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Por conta, a transferência é débito na conta de onde sai
                      (ex.: cartão → banco tira do cartão) e crédito na conta
                      que recebe — o saldo de cada conta fecha. Nos totais e
                      por forma ela não conta (não é dinheiro novo). */}
                  {contasComMovimento.map((c) => {
                    const entrada = c.entrada + c.transferenciaEntrada;
                    const saida = c.saida + c.transferenciaSaida;
                    return (
                      <TableRow key={c.idconta}>
                        <TableCell sx={{ fontWeight: 600 }}>{c.titular}</TableCell>
                        <TableCell>{TIPO_CONTA[c.tipo] || c.tipo}</TableCell>
                        <TableCell align="right" sx={{ ...celNum, color: 'success.main' }}>
                          {formatarMoeda(entrada)}
                          {c.transferenciaEntrada > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">transf. {formatarMoeda(c.transferenciaEntrada)}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ ...celNum, color: 'error.main' }}>
                          {formatarMoeda(saida)}
                          {c.transferenciaSaida > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">transf. {formatarMoeda(c.transferenciaSaida)}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ ...celNum, fontWeight: 700 }}>{formatarMoeda(entrada - saida)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {dados && (
          <Typography variant="caption" color="text.secondary">
            Transferências entre contas no período: <b>{formatarMoeda(dados.totais.transferencias)}</b> (recebíveis → banco,
            caixa → cofre, lançamento entre contas, abertura/fechamento de sessão). Na tabela por conta elas aparecem como saída
            de uma conta e entrada na outra; nos totais e por forma de pagamento não são somadas de novo, porque o valor já entrou
            quando foi recebido. Taxas de maquininha/boleto contam como saída.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" disableElevation startIcon={<PrintRoundedIcon />} onClick={imprimir} disabled={!dados || formasMarcadas.length === 0}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};
